import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@vehix/portal/components/ui/card';
import { Activity, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { Progress } from '@vehix/portal/components/ui/progress';

interface WorkflowHealthDashboardProps {
  executionHistory: any[];
  isLoading: boolean;
}

export const WorkflowHealthDashboard: React.FC<WorkflowHealthDashboardProps> = ({
  executionHistory,
  isLoading
}) => {
  const stats = useMemo(() => {
    if (!executionHistory || executionHistory.length === 0) {
      return {
        activeJobs: 0,
        successRate: 0,
        total24h: 0,
        failed24h: 0,
        lastExecution: null
      };
    }

    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const recentExecutions = executionHistory.filter(
      exec => new Date(exec.started_at) > twentyFourHoursAgo
    );

    const active = executionHistory.filter(exec => {
      const status = exec.status?.toLowerCase() || '';
      return status === 'running' || status === 'in_progress' || status === 'pending';
    }).length;

    const completed = recentExecutions.filter(exec => {
      const status = exec.status?.toLowerCase() || '';
      return status === 'completed' || status === 'success' || status === 'succeeded';
    }).length;

    const failed = recentExecutions.filter(exec => {
      const status = exec.status?.toLowerCase() || '';
      return status === 'failed' || status === 'error';
    }).length;

    const total = completed + failed;
    const successRate = total > 0 ? Math.round((completed / total) * 100) : 100;

    const lastExecution = executionHistory.length > 0 ? executionHistory[0] : null;

    return {
      activeJobs: active,
      successRate,
      total24h: total,
      failed24h: failed,
      lastExecution
    };
  }, [executionHistory]);

  if (isLoading) {
    return (
      <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6'>
        {[1, 2, 3, 4].map(i => (
          <Card key={i} className='animate-pulse'>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
              <div className='h-4 w-24 bg-muted rounded' />
            </CardHeader>
            <CardContent>
              <div className='h-8 w-16 bg-muted rounded' />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6'>
      <Card>
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
          <CardTitle className='text-sm font-medium'>Active Jobs</CardTitle>
          <Activity
            className={`h-4 w-4 ${stats.activeJobs > 0 ? 'text-blue-500 animate-pulse' : 'text-muted-foreground'}`}
          />
        </CardHeader>
        <CardContent>
          <div className='text-2xl font-bold'>{stats.activeJobs}</div>
          <p className='text-xs text-muted-foreground'>Currently running workflows</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
          <CardTitle className='text-sm font-medium'>Success Rate (24h)</CardTitle>
          {stats.successRate >= 90 ? (
            <CheckCircle className='h-4 w-4 text-green-500' />
          ) : stats.successRate >= 70 ? (
            <AlertTriangle className='h-4 w-4 text-yellow-500' />
          ) : (
            <XCircle className='h-4 w-4 text-red-500' />
          )}
        </CardHeader>
        <CardContent>
          <div className='text-2xl font-bold'>{stats.successRate}%</div>
          <Progress
            value={stats.successRate}
            className={`h-2 mt-2 ${
              stats.successRate >= 90
                ? 'bg-green-100 [&>div]:bg-green-500'
                : stats.successRate >= 70
                  ? 'bg-yellow-100 [&>div]:bg-yellow-500'
                  : 'bg-red-100 [&>div]:bg-red-500'
            }`}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
          <CardTitle className='text-sm font-medium'>Failed Jobs (24h)</CardTitle>
          <AlertTriangle
            className={`h-4 w-4 ${stats.failed24h > 0 ? 'text-red-500' : 'text-muted-foreground'}`}
          />
        </CardHeader>
        <CardContent>
          <div className='text-2xl font-bold'>{stats.failed24h}</div>
          <p className='text-xs text-muted-foreground'>Out of {stats.total24h} total runs</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
          <CardTitle className='text-sm font-medium'>Last Execution</CardTitle>
          <Clock className='h-4 w-4 text-muted-foreground' />
        </CardHeader>
        <CardContent>
          <div className='text-lg font-bold truncate'>
            {stats.lastExecution
              ? new Date(stats.lastExecution.started_at).toLocaleTimeString()
              : 'N/A'}
          </div>
          <p className='text-xs text-muted-foreground truncate'>
            {stats.lastExecution
              ? new Date(stats.lastExecution.started_at).toLocaleDateString()
              : 'No recent activity'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
